import React, { useEffect, useRef, useState } from 'react';
import './contact-messages-landing.styles.scss';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { getPaginatedQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ContactMessageView from './contact-message-view.component';

const PURPOSE_LABELS = {
  'quote': 'Enterprise Quote',
  'feature': 'Feature Idea',
  'problem': 'Problem Report',
  'hey': 'Just Saying Hey',
};

const PAGE_SIZE = 20;

const AdminContactMessages = () => {

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    changeDocumentTitle('Gamably Admin | Contact Messages');
    getMessages();
  }, [])

  async function getMessages() {
    setLoading(true);
    const res = await getPaginatedQueryFromFirestore({
      'path': 'contact_general',
      'orderBy': { 'field': 'ts_created', 'direction': 'desc' },
      'loadLimit': PAGE_SIZE,
    })
    lastDocRef.current = res.lastDoc;
    setMessages(res.data);
    setHasMore(res.hasMore);
    setLoading(false);
  }

  async function loadMore() {
    if (!lastDocRef.current) return;
    setLoadingMore(true);
    const res = await getPaginatedQueryFromFirestore({
      'path': 'contact_general',
      'orderBy': { 'field': 'ts_created', 'direction': 'desc' },
      'loadLimit': PAGE_SIZE,
      'startAfterDoc': lastDocRef.current,
    })
    lastDocRef.current = res.lastDoc;
    setMessages(prev => [...prev, ...res.data]);
    setHasMore(res.hasMore);
    setLoadingMore(false);
  }

  return (
    <div className='admin-contact-messages'>
      <div className='admin-contact-content'>
        <div className='section-title'>
          <span>Contact Messages</span>
        </div>
        <Routes>
          <Route path="" element={
            <div className='messages-list-container'>
              {
                (loading)
                  ?
                  <div className='center-col'>
                    <div className="g-space-2"></div>
                    <ThreeDotsLoader />
                  </div>
                  :
                  (messages.length === 0)
                    ?
                    <div className='center-col'>
                      <div className="g-space-2"></div>
                      <h4>No contact messages yet.</h4>
                    </div>
                    :
                    <>
                      <table className='g-table'>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Name</th>
                            <th>Purpose</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            messages.map(m => (
                              <tr key={m.id}>
                                <td>{m.ts_created ? format(new Date(m.ts_created), 'Pp') : '—'}</td>
                                <td>{m.fName} {m.lName}</td>
                                <td>{PURPOSE_LABELS[m.purpose] ?? m.purpose ?? '—'}</td>
                                <td>
                                  <span className={`status-badge ${m.status ?? 'pending'}`}>
                                    {m.status ?? 'pending'}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className='g-button small-btn'
                                    onClick={() => navigate(m.id)}>
                                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                                    <span>Manage</span>
                                  </button>
                                </td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                      <div className="g-space-1"></div>
                      {
                        (hasMore) &&
                        <div className='center-col'>
                          {
                            (loadingMore)
                              ?
                              <button className='g-button disabled'>
                                <ThreeDotsLoader />
                              </button>
                              :
                              <button
                                className='g-button'
                                onClick={() => loadMore()}>
                                Load More...
                              </button>
                          }
                        </div>
                      }
                    </>
              }
            </div>
          } />
          <Route path=":contactId" element={<ContactMessageView />} />
        </Routes>
      </div>
    </div>
  )
}

export default AdminContactMessages;
